// Web Push plumbing shared by Settings and app start-up.

import { api, ApiError } from './api.ts';
import { devicePrefs } from './device.ts';

export const pushSupported = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export function keyBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const b64 = b64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (b64url.length % 4)) % 4);
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function sameKey(sub: PushSubscription, key: Uint8Array): boolean {
  const k = sub.options?.applicationServerKey;
  if (!k) return true; // can't tell — assume fine
  const a = new Uint8Array(k);
  return a.length === key.length && a.every((v, i) => v === key[i]);
}

/** A subscription for the server's current key (resubscribes if the key changed, or when `fresh`). */
export async function ensureSubscription(publicKey: string, fresh = false): Promise<PushSubscription> {
  const reg = await navigator.serviceWorker.ready;
  const key = keyBytes(publicKey);
  const existing = await reg.pushManager.getSubscription();
  if (existing && !fresh && sameKey(existing, key)) return existing;
  await existing?.unsubscribe().catch(() => undefined);
  return reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
}

/**
 * Register this device's subscription with the server. If the server knows
 * it's dead (the push service turned it down), throw it away and make a
 * brand-new one — re-sending the same dead subscription is what made alerts
 * "expire" every time.
 */
export async function registerPush(opts: { publicKey: string; device: string; person?: string; prefs?: Record<string, boolean>; fresh?: boolean }): Promise<PushSubscription> {
  const send = (sub: PushSubscription) => api('/api/push/subscribe', { body: { subscription: sub.toJSON(), device: opts.device, person: opts.person ?? null, prefs: opts.prefs } });
  let sub = await ensureSubscription(opts.publicKey, opts.fresh);
  try {
    await send(sub);
  } catch (e) {
    if (!(e instanceof ApiError && e.status === 409)) throw e;
    sub = await ensureSubscription(opts.publicKey, true);
    await send(sub);
  }
  return sub;
}

/** Keep the server's copy fresh — push services rotate endpoints, and the server's key can change. */
export async function refreshPushSubscription(): Promise<void> {
  if (!pushSupported || Notification.permission !== 'granted') return;
  const d = devicePrefs();
  if (!d.key || !(await currentSubscription().catch(() => null))) return;
  try {
    const { publicKey, subscribed } = await api<{ publicKey: string; subscribed: boolean }>(`/api/push?device=${encodeURIComponent(d.deviceId)}`);
    // Turning alerts off removes the browser's subscription too, so one that
    // still exists means "wanted". If the server has dropped it, the push
    // service said it was dead, so start again with a fresh one.
    await registerPush({ publicKey, device: d.deviceId, person: d.personId, fresh: !subscribed });
  } catch {
    /* offline — try next launch */
  }
}
